import '../styles/HistoryRunList.css';
import React, { useMemo, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'complete', label: 'Complete' },
  { key: 'failed', label: 'Failed' },
  { key: 'running', label: 'Running' },
];

const BADGE_CLASS = {
  complete: 'hrl-badge--complete',
  failed: 'hrl-badge--failed',
  running: 'hrl-badge--running',
};

const BADGE_LABEL = {
  complete: 'Complete',
  failed: 'Failed',
  running: 'Running',
};

function stateKey(state) {
  return String(state || '').toLowerCase();
}

/** Absolute date from the ISO createdAt, in the local store's wall-clock format. */
function formatStamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function matchesQuery(run, q) {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return `${run.name || ''} ${run.target || ''}`.toLowerCase().includes(needle);
}

function StateBadge({ state }) {
  const key = stateKey(state);
  const cls = BADGE_CLASS[key] || 'hrl-badge--pending';
  const label = BADGE_LABEL[key] || (state ? String(state) : 'Unknown');
  return (
    <span className={`hrl-badge ${cls}`}>
      {key === 'running' && <span className="hrl-badge__dot" aria-hidden="true" />}
      {label}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="hrl-rows" aria-hidden="true">
      {[0, 1, 2, 3].map((n) => (
        <div className="hrl-skel" key={n}>
          <div className="hrl-skel__stack">
            <span className="hrl-skel__bar hrl-skel__bar--wide" />
            <span className="hrl-skel__bar hrl-skel__bar--short" />
          </div>
          <span className="hrl-skel__bar hrl-skel__config" />
          <span className="hrl-skel__bar hrl-skel__bar--short" />
          <span className="hrl-skel__pill" />
        </div>
      ))}
    </div>
  );
}

export default function HistoryRunList() {
  const { data, loading, error, refresh } = useProjectCollection('test_run');
  const [stateFilter, setStateFilter] = useState('all');
  const [query, setQuery] = useState('');

  const runs = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return [...list].sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
  }, [data]);

  const counts = useMemo(() => {
    const base = { all: runs.length, complete: 0, failed: 0, running: 0 };
    runs.forEach((r) => {
      const k = stateKey(r.state);
      if (Object.prototype.hasOwnProperty.call(base, k) && k !== 'all') base[k] += 1;
    });
    return base;
  }, [runs]);

  const visible = useMemo(
    () => runs.filter((r) => (stateFilter === 'all' || stateKey(r.state) === stateFilter) && matchesQuery(r, query)),
    [runs, stateFilter, query],
  );

  const isReadError = Boolean(error) && !loading;
  const hasRows = runs.length > 0;

  let body;
  if (loading && !hasRows) {
    body = <SkeletonRows />;
  } else if (isReadError && !hasRows) {
    body = null;
  } else if (!hasRows) {
    body = (
      <div className="hrl-state">
        <span className="hrl-state__label">Local store · test_run</span>
        <h3 className="hrl-state__title">No history yet</h3>
        <p className="hrl-state__body">
          Runs appear here after an authorized test is configured and saved. When one is recorded, it lands in this
          list with its target, configuration and resolution time.
        </p>
      </div>
    );
  } else if (visible.length === 0) {
    body = (
      <p className="hrl-nomatch">
        No run matches <strong>{stateFilter === 'all' ? 'the current scan' : `${FILTERS.find((f) => f.key === stateFilter)?.label} · ${query.trim() || 'filter'}`}</strong>. Pick another state or clear the scan.
      </p>
    );
  } else {
    body = (
      <ul className="hrl-rows">
        {visible.map((run) => (
          <li className="hrl-row" key={run.id}>
            <div className="hrl-row__main">
              <div className="hrl-row__top">
                <h3 className="hrl-row__name">{run.name}</h3>
              </div>
              <div className="hrl-row__meta">
                <span className="hrl-row__target">{run.target}</span>
              </div>
            </div>
            <div className="hrl-row__config">
              <span className="hrl-row__config-label">Config</span>
              <span className="hrl-row__config-value">{run.configurationSummary || '—'}</span>
            </div>
            <div className="hrl-row__side">
              <StateBadge state={run.state} />
              <time className="hrl-row__time" dateTime={run.createdAt}>
                {formatStamp(run.createdAt)}
              </time>
            </div>
            <a className="hrl-open" href={`/results?id=${encodeURIComponent(run.id)}`}>
              Open Results
              <span className="hrl-open__arrow" aria-hidden="true">→</span>
            </a>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <section className="hrl-root" aria-labelledby="hrl-heading">
      <div className="hrl-filter" role="region" aria-label="Filter run history">
        <div className="hrl-filter__inner" data-content-rail="workspace">
          <div className="hrl-chips" role="group" aria-label="Filter by state">
            <span className="hrl-chips__label">State</span>
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className="hrl-chip"
                aria-pressed={stateFilter === f.key}
                onClick={() => setStateFilter(f.key)}
              >
                {f.label}
                <span className="hrl-chip__count" aria-hidden="true">{counts[f.key]}</span>
              </button>
            ))}
          </div>

          <div className="hrl-scan">
            <label className="hrl-scan__label" htmlFor="hrl-scan-input">Scan</label>
            <div className="hrl-scan__field">
              <input
                id="hrl-scan-input"
                className="hrl-scan__input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Run name or target"
                autoComplete="off"
                spellCheck={false}
              />
              {query && (
                <button
                  type="button"
                  className="hrl-scan__clear"
                  aria-label="Clear scan field"
                  onClick={() => setQuery('')}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div data-content-rail="workspace">
        <h2 id="hrl-heading" className="hrl-visually-hidden">Run history</h2>
        <div className="hrl-panel">
          <div className="hrl-panel__head">
            <span className="hrl-panel__title">
              Runs
              <span className="hrl-panel__count" aria-live="polite">
                {loading && !hasRows ? 'reading…' : `${visible.length}/${runs.length}`}
              </span>
            </span>
            <span className="hrl-sticker">Authorized target only</span>
          </div>

          {isReadError && hasRows && (
            <p className="hrl-state__reason hrl-state__reason--inline" role="status">
              Could not refresh the local store: {error}. Showing the last rows read.
            </p>
          )}

          {isReadError && !hasRows ? (
            <div className="hrl-state hrl-state--error" role="alert">
              <span className="hrl-state__label">Local store · test_run</span>
              <h3 className="hrl-state__title hrl-state__title--error">History could not be read</h3>
              <p className="hrl-state__reason">{String(error)}</p>
              <button type="button" className="hrl-retry" onClick={() => refresh()}>
                Retry read
              </button>
            </div>
          ) : (
            body
          )}
        </div>
      </div>
    </section>
  );
}